import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  Card,
  CardContent,
  Grid,
  TextField,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { adminApiService } from '../services/api';
import { useAuth } from '../auth/AuthProvider';
import { AdminReporter } from '../types';

export const Reporters: React.FC = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [reporters, setReporters] = useState<AdminReporter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [nameFilter, setNameFilter] = useState<string>('');
  const [addressStateFilter, setAddressStateFilter] = useState<string>('');
  const [workingStateFilter, setWorkingStateFilter] = useState<string>('');
  const [includeNonPrimary, setIncludeNonPrimary] = useState<boolean>(true);
  const [preselectApplied, setPreselectApplied] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const isSchedulingAdmin = hasRole('Scheduling.Admin');
  const isAssigner = hasRole('Assigner');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.getAdminReporters();
      if (response.success && response.data) {
        setReporters(response.data);
      }
    } catch {
      setError('Failed to load reporters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // For single-state assigners, pre-select the working state filter on first load.
  useEffect(() => {
    if (preselectApplied || !isAssigner || isSchedulingAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await adminApiService.getMyAssigner();
        if (cancelled) return;
        if (me.success && me.data && me.data.states.length === 1) {
          setWorkingStateFilter(me.data.states[0]);
        }
        setPreselectApplied(true);
      } catch {
        setPreselectApplied(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isAssigner, isSchedulingAdmin, preselectApplied]);

  const addressStateOptions = Array.from(
    new Set(reporters.map(r => r.reporterState).filter((s): s is string => !!s))
  ).sort();

  const workingStateOptions = Array.from(
    new Set(
      reporters.flatMap(r => r.statePreferences?.map(p => p.state) || []).filter((s): s is string => !!s)
    )
  ).sort();

  const normalizedNameFilter = nameFilter.trim().toLowerCase();
  const filteredReporters = reporters.filter(r => {
    if (addressStateFilter && r.reporterState !== addressStateFilter) return false;
    if (workingStateFilter) {
      const prefs = r.statePreferences || [];
      const match = includeNonPrimary
        ? prefs.some(p => p.state === workingStateFilter)
        : prefs.some(p => p.state === workingStateFilter && p.isPrimary === true);
      if (!match) return false;
    }
    if (normalizedNameFilter && !(r.name || '').toLowerCase().includes(normalizedNameFilter)) return false;
    return true;
  });

  return (
    <Layout pageTitle="Reporters">
      <Box>
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Name"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>State (Address)</InputLabel>
                    <Select
                      value={addressStateFilter}
                      onChange={(e) => setAddressStateFilter(e.target.value)}
                      label="State (Address)"
                    >
                      <MenuItem value=""><em>Any</em></MenuItem>
                      {addressStateOptions.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>State (Working)</InputLabel>
                    <Select
                      value={workingStateFilter}
                      onChange={(e) => setWorkingStateFilter(e.target.value)}
                      label="State (Working)"
                    >
                      <MenuItem value=""><em>Any</em></MenuItem>
                      {workingStateOptions.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeNonPrimary}
                        onChange={(e) => setIncludeNonPrimary(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include non-primary"
                    disabled={!workingStateFilter}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : filteredReporters.length === 0 ? (
          <Alert severity="info">No reporters found.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>State (Address)</strong></TableCell>
                  <TableCell><strong>State(s) (Working)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReporters.map((reporter) => (
                  <TableRow
                    key={reporter.reporterId}
                    hover
                    onClick={() => navigate(`/reporters/${reporter.reporterId}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {reporter.name || reporter.reporterId}
                        </Typography>
                        {reporter.isAgency && (
                          <Tooltip title="Agency">
                            <Chip label="A" size="small" color="secondary" sx={{ height: 20, width: 20, fontSize: '0.65rem', fontWeight: 700, '& .MuiChip-label': { px: 0 } }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{reporter.email || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{reporter.location || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{reporter.reporterState || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const prefs = reporter.statePreferences || [];
                        if (prefs.length === 0) {
                          return <Typography variant="body2">—</Typography>;
                        }
                        const hasPrimary = prefs.some(p => p.isPrimary);
                        if (!hasPrimary) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                icon={<WarningAmberIcon sx={{ fontSize: '0.85rem' }} />}
                                label="No primary set"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                              />
                              <Typography variant="body2">{prefs.map(p => p.state).join(', ')}</Typography>
                            </Box>
                          );
                        }
                        return (
                          <Typography variant="body2">
                            {prefs.map((p, i) => (
                              <React.Fragment key={p.state}>
                                {i > 0 && ', '}
                                {p.isPrimary ? <strong>{p.state}</strong> : p.state}
                              </React.Fragment>
                            ))}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};
